import { useCallback } from 'react'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  any,
  ascend,
  assoc,
  descend,
  omit,
  path,
  prop,
  propEq,
  sortWith
} from 'ramda'
import { gql, useQuery } from '@apollo/client'
import {
  Bleed,
  Box,
  Dialog,
  ResponsiveRender,
  Stack,
  Text
} from '@machobear/design-system'

import { breakpoints, colors } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import { getLocalBuildingUrl } from '@/utils/building'
import useMediaQuery from '@/hooks/use-media-query'
import useSessionComplete from '@/hooks/use-session-complete'
import { ListingCardItem } from '@/features/ListingsMap/map-list/listing-card-item'
import CdnImage from '@/components/cdn-image'
import { useImpressionTracker } from '@/components/ImpressionProvider/ImpressionProvider'
import { useListImpressionTracking } from '@/components/ImpressionProvider/useListImpressionTracking'
import { useTermsDialogContext } from '@/components/terms-dialog/context'
import { Label, ParagraphRegular } from '@/components/text'

import defaultBuildingImage from './default-building.jpg'

export const BUILDING_DATA_QUERY = gql`
  query ($input: BuildingDetailInput!) {
    buildingDetail(input: $input) {
      listing_id
      city
      state
      price
      building_name
      price_frequency
      bedrooms
      bathrooms
      full_street_name
      unit_type_scope_txt_id
      verified_state_machine
      cover_photo_aws_s3_key
      size
      building_type
      hide_unit_number
      street_name
      allow_applications
      public_viewing
      gr_min_price
      gr_max_price
      gr_unit
      gr_count
      gr_min_size
      gr_max_size
      unit_type_txt_id
      unit_files {
        aws_s3_key
        position
        tag
      }
      building_files {
        aws_s3_key
        tag
      }
      search_deals
      previous_price
      price_change_status
    }
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    overflow: hidden;
    height: calc(100vh - 200px);
    overflow: auto;
  }
`

const LeftSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;
  background-color: ${colors.grey96};
  min-height: 550px;
  max-width: 320px;
  padding: 32px;
  padding-top: 40px;
  margin-top: -6px;
  border-radius: 20px 0 0 20px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    min-height: max-content;
    max-width: unset;
    background-color: ${colors.white};
    gap: 8px;
    border-bottom: 1px solid ${colors.grey88};
    padding: 0;
    border-radius: 0;
    position: fixed;
    width: 100%;
    z-index: 100;
  }
`

const RightSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    overflow: hidden;
  }
`

const StickyHeader = styled.div`
  padding: 32px 32px 24px;
`

const UnitsAvailableWrapper = styled.div`
  display: flex;
  align-items: center;
  color: ${colors.black};
`

const ScrollWrapper = styled.div`
  padding: 4px 32px 32px;
  max-height: 550px;
  overflow-y: auto;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    max-height: 100%;
    padding: 24px 16px 10px;
  }
`

const PhotoContainer = styled.div`
  width: 100%;
  height: 180px;
  overflow: hidden;
  border-radius: 8px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  animation: fadeIn ease-in-out 0.2s;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
`

const ListingLinkText = styled(ParagraphRegular)`
  a {
    font-weight: 500;
    text-decoration: none;
    color: ${colors.link};
  }

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 14px;
    line-height: 20px;
  }
`

const getCover = images => {
  if (!images?.length) return false
  const cover = images.find(({ tag }) => tag === 'cover_photo')
  return cover?.aws_s3_key ?? images[0].aws_s3_key
}

export const BuildingListModal = ({
  id,
  building_id,
  building,
  filters,
  router,
  visible,
  onClose,
  trackImpression = {}
}) => {
  const { t } = useTranslation()
  const isLoggedIn = useSessionComplete()
  const { addImpression } = useImpressionTracker()

  const { start } = useListImpressionTracking({
    id: `building-modal-${building_id}`,
    enabled: visible,
    minVisibleDuration: 1000,
    threshold: 0.5,
    containerSelector: `[data-impression-container="true"]`,
    manualStart: true
  })

  const containerRef = useCallback(
    node => {
      if (node && visible) {
        start(node)
      }
    },
    [visible, start]
  )

  const { isLoading, checkAgreementMismatch } = useTermsDialogContext()
  const isAgreementMismatched = checkAgreementMismatch('trreb_agreements')
  const isLoadingAgreements = isLoading && isLoggedIn

  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const { data: { buildingDetail = [] } = {} } = useQuery(BUILDING_DATA_QUERY, {
    skip: !building_id,
    variables: {
      input: {
        ...omit(
          [
            'page',
            'page_size',
            'featured',
            'sort',
            'cities',
            'availability_date',
            'custom_availability_date'
          ],
          filters
        ),
        building_id
      }
    },
    onCompleted: data => {
      if (trackImpression) {
        try {
          for (const building of data.buildingDetail) {
            addImpression(building.listing_id, trackImpression)
          }
        } catch (error) {
          console.error('Error tracking impression', error)
        }
      }
    }
  })

  const firstListing = path([0], buildingDetail)
  if (!firstListing) return null

  const isApartmentBuilding = firstListing.building_type === 'highrise'

  const buildingImage =
    getCover(firstListing?.building_files) ||
    firstListing?.cover_photo_aws_s3_key
  const buildingCover = buildingImage || defaultBuildingImage

  const hideStreetNumber =
    firstListing.building_type === 'house' &&
    any(propEq('hide_unit_number', true), buildingDetail)

  const streetName = hideStreetNumber
    ? firstListing.street_name
    : firstListing.full_street_name

  const buildingName = firstListing.building_name || streetName

  const getListingsByPrice = listings => {
    const withSortPrice = listings.map(listing => {
      const sortPrice = listing.gr_min_price || listing.price
      return assoc('sort_price', sortPrice)(listing)
    })
    return sortWith([
      descend(prop('price_frequency')),
      ascend(prop('sort_price')),
      ascend(prop('listing_id'))
    ])(withSortPrice)
  }
  const listingsByPrice = getListingsByPrice(buildingDetail)

  const UnitsAvailableText = () => (
    <UnitsAvailableWrapper>
      <Label fontSize="14px">
        <Trans
          i18nKey="b.listings.view.building_modal_units_available.text"
          values={{ numberOfUnits: buildingDetail.length }}
          defaults="{{ numberOfUnits }} Units Available"
        />
      </Label>
    </UnitsAvailableWrapper>
  )

  const MobileHeader = () => {
    return (
      <ResponsiveRender below="tablet">
        <Box marginLeft="small" marginBottom="gutter">
          <Stack space="medium">
            <Text weight="strong" size="large">
              {buildingName}
            </Text>
            <Text>{`${streetName}, ${firstListing.city}, ${firstListing.state}`}</Text>
            {isApartmentBuilding && (
              <Section>
                <ListingLinkText>
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href={
                      isEmptyOrNull(building)
                        ? `/building/${building_id}`
                        : getLocalBuildingUrl(building, router.locale)
                    }
                    id={
                      isLoggedIn
                        ? 'logged-view-building-details-click'
                        : 'listing-view-building-details-click'
                    }
                  >
                    {t(
                      'b.listings.view.view_building_details.button',
                      'View building details'
                    )}
                  </a>
                </ListingLinkText>
              </Section>
            )}
          </Stack>
        </Box>
      </ResponsiveRender>
    )
  }

  const DesktopHeader = () => {
    return (
      <ResponsiveRender above="mobile">
        <Section>
          <PhotoContainer>
            <CdnImage
              src={buildingCover}
              sizes={'345px'}
              layout="fill"
              objectFit="cover"
              placeholder={'shimmer'}
              isCustom
            />
          </PhotoContainer>
        </Section>
        <Stack space="small">
          <Text weight="strong" size="large">
            {buildingName}
          </Text>
          <Stack space="none">
            <Text tone="secondary" baseline={false}>
              {streetName}
            </Text>
            <Text
              tone="secondary"
              baseline={false}
            >{`${firstListing.city}, ${firstListing.state}`}</Text>
          </Stack>
        </Stack>
        {isApartmentBuilding && (
          <Section>
            <ListingLinkText>
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={
                  isEmptyOrNull(building)
                    ? `/building/${building_id}`
                    : getLocalBuildingUrl(building, router.locale)
                }
                id={
                  isLoggedIn
                    ? 'logged-view-building-details-click'
                    : 'listing-view-building-details-click'
                }
              >
                {t(
                  'b.listings.view.view_building_details.button',
                  'View building details'
                )}
              </a>
            </ListingLinkText>
          </Section>
        )}
      </ResponsiveRender>
    )
  }

  return (
    <Dialog id={id} open={visible} onClose={onClose} width="large">
      <Bleed
        top={{ mobile: 'large', tablet: 'xxxlarge' }}
        horizontal={{ mobile: 'gutter', tablet: 'large' }}
        bottom={{ mobile: 'none', tablet: 'large' }}
      >
        <MobileHeader />
        <Wrapper>
          <LeftSection>
            <DesktopHeader />
          </LeftSection>
          <RightSection>
            <ResponsiveRender above="mobile">
              <StickyHeader>
                <UnitsAvailableText />
              </StickyHeader>
            </ResponsiveRender>
            <ScrollWrapper>
              <ResponsiveRender below="tablet">
                <Box paddingBottom="gutter">
                  <UnitsAvailableText />
                </Box>
              </ResponsiveRender>
              <Box
                ref={containerRef}
                display="grid"
                width="full"
                gap="gutter"
                data-impression-container="true"
              >
                {listingsByPrice.map(listing => {
                  const commonProps = {
                    id: listing.listing_id,
                    listing,
                    isFeatured: false,
                    hasSession: isLoggedIn,
                    isAgreementMismatched,
                    isAgreementLoading: isLoadingAgreements,
                    suppressUnitAvailable: true,
                    onListingHover: () => {},
                    onClick: () => {}
                  }

                  if (isMobile) {
                    return (
                      <ListingCardItem
                        key={listing.listing_id}
                        trackImpression={{
                          id: listing.listing_id,
                          enabled: true,
                          page: 'listings',
                          section: 'building-modal',
                          sponsored: false
                        }}
                        {...commonProps}
                      />
                    )
                  }

                  return (
                    <ListingCardItem
                      key={listing.listing_id}
                      trackImpression={{
                        id: listing.listing_id,
                        enabled: true,
                        page: 'listings',
                        section: 'building-modal',
                        sponsored: false
                      }}
                      {...commonProps}
                      horizontal={true}
                    />
                  )
                })}
              </Box>
            </ScrollWrapper>
          </RightSection>
        </Wrapper>
      </Bleed>
    </Dialog>
  )
}
